import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { resetCloudPrepState } from '../../../../redux/Slices/CloudPrepSlice';
import { setIsStartTest } from '../../../../redux/Slices/QuestionsSlice';
import { useAppDispatch, useAppSelector } from '../../../../redux/store/hooks';
import {
  useSaveCloudPrepTestMutation,
  useEndCloudPrepTestMutation
} from '../../../../services/cloudPrepApi';
import Button from '../../../Common/Button';
import CommonDialog from '../../../Common/Dialog';
import TestHeader from '../../../Features/PracticeTest/TestHeader';

import NavigationBar from './NavigationBar';
import QuestionCard from './QuestionCard';

const TestScreen = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [userAnswers, setUserAnswers] = useState<Record<string, string[]>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isExitDialog, setIsExitDialog] = useState(false);

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { testData: overallTestData, selectedTestData } = useAppSelector(
    state => state.root.cloudPrep
  );
  const timer = useAppSelector(state => state.root.startFulltest.currentTime);

  const [saveCloudPrepTest] = useSaveCloudPrepTestMutation();
  const [endCloudPrepTest] = useEndCloudPrepTestMutation();

  const testData =
    overallTestData?.cloudPrepTestDTO?.cloudPrepExamPatternDTO?.exam?.[0];

  const currentQuestion = testData?.generatedSectionQuestions?.[currentIndex];

  const handleAnswerSelect = (questionId: string, selected: string[]) => {
    setUserAnswers(prev => ({ ...prev, [questionId]: selected }));
  };

  const handleNext = () => {
    if (currentIndex < (testData?.generatedSectionQuestions?.length ?? 0) - 1)
      setCurrentIndex(currentIndex + 1);
  };

  const handleBack = () => {
    if (currentIndex > 0) setCurrentIndex(currentIndex - 1);
  };
  useEffect(() => {
    if (timer === 0 && !isSubmitting) {
      handleSubmit();
    }
  }, [timer]);

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = ''; // required for Chrome
    };

    const handleUnload = () => {
      // After user confirms reload
      window.sessionStorage.setItem('redirectAfterReload', 'true');
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('unload', handleUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('unload', handleUnload);
    };
  }, []);

  useEffect(() => {
    if (!selectedTestData?.answers) return;
    const restored: Record<string, string[]> = {};
    Object.entries(selectedTestData.answers).forEach(
      ([questionId, options]: any) => {
        restored[questionId] = options.map((o: any) => o.answerValue);
      }
    );
    setUserAnswers(restored);
  }, [selectedTestData]);

  const buildSavePayload = () => {
    const formattedAnswers: Record<string, any[]> = {};
    let totalAttempted = 0;
    let totalCorrect = 0;

    Object.entries(userAnswers).forEach(([questionId, selectedValues]) => {
      const question = testData?.generatedSectionQuestions?.find(
        (q: any) => q.id === questionId
      );
      if (!question) return;

      const selectedOptions = question.options.filter((opt: any) =>
        selectedValues.includes(opt.answerValue)
      );
      if (selectedOptions.length > 0) totalAttempted++;
      selectedOptions.forEach((opt: any) => {
        if (opt.correctAnswer === true) totalCorrect++;
      });

      formattedAnswers[questionId] = selectedOptions.map((opt: any) => ({
        answerOption: opt.answerOption,
        answerValue: opt.answerValue,
        correctAnswer: opt.correctAnswer,
        active: true
      }));
    });

    return {
      id: overallTestData?.id,
      userId: selectedTestData?.userId,
      companyId:
        overallTestData?.cloudPrepTestDTO?.cloudPrepExamPatternDTO?.company,
      testName: overallTestData?.cloudPrepTestDTO?.testName,
      examPatternId:
        overallTestData?.cloudPrepTestDTO?.cloudPrepExamPatternDTO?.id,
      cloudPrepTestDTO: {
        id: overallTestData?.cloudPrepTestDTO?.id,
        testName: overallTestData?.cloudPrepTestDTO?.testName,
        cloudPrepExamPatternDTO:
          overallTestData?.cloudPrepTestDTO?.cloudPrepExamPatternDTO
      },
      testType: selectedTestData?.testType,
      testStatus: 'COMPLETED',
      isTestCompleted: true,
      isActive: true,
      totalQuestionsGiven: testData?.generatedSectionQuestions?.length || 0,
      totalQuestionsAttempted: totalAttempted,
      totalQuestionsCorrect: totalCorrect,
      answers: formattedAnswers,
      updatedDate: new Date().toISOString()
    };
  };

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);

      if (!overallTestData?.id || !selectedTestData?.userId) return;
      await saveCloudPrepTest({
        userTestId: overallTestData.id,
        body: buildSavePayload()
      }).unwrap();

      await endCloudPrepTest({
        userTestId: overallTestData.id,
        testType: selectedTestData?.testType
      }).unwrap();

      dispatch(setIsStartTest(false));
      dispatch(resetCloudPrepState());

      navigate(
        `/cloud-prep-results/${overallTestData.id}/${selectedTestData.userId}/true`
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    window.history.pushState(null, '', window.location.href);

    const handleBackButton = () => {
      setIsExitDialog(true);
      window.history.pushState(null, '', window.location.href);
    };

    window.addEventListener('popstate', handleBackButton);

    return () => {
      window.removeEventListener('popstate', handleBackButton);
    };
  }, []);

  const handleSaveAndExit = () => {
    dispatch(setIsStartTest(false));
    dispatch(resetCloudPrepState());
    setIsExitDialog(false);
    navigate('/cloud-prep');
  };

  if (isSubmitting) {
    return (
      <div className='flex h-screen w-full items-center justify-center bg-white'>
        <div className='text-center'>
          <p className='animate-pulse text-2xl font-bold text-blue-700'>
            Generating your Results...
          </p>
          <p className='mt-2 text-gray-500'>Please wait a moment.</p>
        </div>
      </div>
    );
  }

  const totalQuestions = testData?.generatedSectionQuestions?.length || 0;
  const isFirst = currentIndex === 0;
  const isLast = currentIndex === totalQuestions - 1;

  return (
    <div className='flex h-screen flex-col overflow-hidden bg-white'>
      <div className='h-[10%]'>
        <TestHeader
          title={overallTestData?.cloudPrepTestDTO?.testName || 'Test Name'}
          time='0:00'
          currentSectionName=''
          shouldShowTimer={false}
          closeFunction={() => setIsExitDialog(true)}
        />
      </div>

      <div className='flex flex-1 flex-col items-center justify-start overflow-y-auto p-4'>
        <div className='w-full md:max-w-4xl 2xl:w-1/2'>
          {currentQuestion && (
            <QuestionCard
              question={currentQuestion}
              selectedAnswers={userAnswers[currentQuestion?.id] || []}
              onAnswerSelect={handleAnswerSelect}
            />
          )}

          <NavigationBar
            questions={testData?.generatedSectionQuestions || []}
            currentIndex={currentIndex}
            handleQuestionJump={(index: number) => setCurrentIndex(index)}
            userAnswers={userAnswers}
          />

          <div className='mt-6 flex w-full justify-between pb-10'>
            <Button
              label='Back'
              handleClickBtn={handleBack}
              disabled={isFirst}
              variant='contained'
            />

            {isLast ? (
              <Button
                label='Submit'
                handleClickBtn={handleSubmit}
                variant='contained'
              />
            ) : (
              <Button
                label='Next'
                handleClickBtn={handleNext}
                variant='contained'
              />
            )}
          </div>
        </div>
      </div>

      <CommonDialog close={() => setIsExitDialog(false)} isOpen={isExitDialog}>
        <div className='flex flex-col items-center p-4'>
          <h2 className='mb-4 text-center text-lg font-semibold'>
            Are you sure you want to leave? Your progress will be saved.
          </h2>
          <Button
            label='Save & Exit'
            handleClickBtn={handleSaveAndExit}
            variant='contained'
          />
        </div>
      </CommonDialog>
    </div>
  );
};

export default TestScreen;
